import type { CircularProgressProps } from '@mui/material/CircularProgress';
import type { LinearProgressProps } from '@mui/material/LinearProgress';
import type { Theme } from '@mui/material/styles';

import { linearProgressClasses } from '@mui/material/LinearProgress';
import { alpha } from '@mui/material/styles';

import type { OnSurfaceMarkFamily } from '../../onSurface';

import { ON_SURFACE_MARK_FAMILIES, onSurfaceMark } from '../../onSurface';

// ----------------------------------------------------------------------

const COLORS = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
] as const;

// ----------------------------------------------------------------------

export function progress(theme: Theme) {
  const rootStyles = (ownerState: LinearProgressProps) => {
    const bufferVariant = ownerState.variant === 'buffer';

    const defaultStyle = {
      [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 4,
      },
      borderRadius: 4,
      ...(bufferVariant && {
        backgroundColor: 'transparent',
      }),
    };

    // The BAR is the family drawn as a mark on its own track and on the page, so
    // it takes the mark step: `main` is a fill, 1.7:1 for `success` on a cream
    // tenant. The track is a wash of the family and stays. A buffer's value bar
    // is `bar1`; its `bar2` is the lighter buffered stretch MUI shades itself,
    // which must not turn into a second value bar.
    const barSelector = `& .${
      bufferVariant
        ? linearProgressClasses.bar1Buffer
        : linearProgressClasses.bar
    }`;

    const colorStyle = COLORS.map((color) => ({
      ...(ownerState.color === color && {
        [barSelector]: { backgroundColor: onSurfaceMark[color] },
        backgroundColor: alpha(theme.palette[color].main, 0.24),
      }),
    }));

    return [defaultStyle, ...colorStyle];
  };

  return {
    // The spinner's arc is a mark drawn on the page, in `currentColor` =
    // `<color>.main` (1.7:1 on a light tenant). `inherit` is left alone: that
    // spinner sits inside a control and takes its ink.
    MuiCircularProgress: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: CircularProgressProps }) =>
          ON_SURFACE_MARK_FAMILIES.includes(
            ownerState.color as OnSurfaceMarkFamily,
          )
            ? { color: onSurfaceMark[ownerState.color as OnSurfaceMarkFamily] }
            : {},
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: LinearProgressProps }) =>
          rootStyles(ownerState),
      },
    },
  };
}
